import { hydrateKendoForm } from "../hydrator.js";
import type {
  HydratorConfig,
  Consideration,
  ConsiderationOption,
} from "../types/index.js";
import { EmptyGuid } from "../types/index.js";
import {
  createHydratorConfig,
  executeScripts,
  serializeForm,
  setUrl,
} from "../utils.js";

let considerationOptions: ConsiderationOption[] = [];

const hydrator: HydratorConfig<Consideration> =
  createHydratorConfig<Consideration>({
    fields: [
      {
        property: "Key",
        selector: "input[name='Key']",
        type: "hidden",
        fallback: "",
      },
      { property: "Name", selector: "#Name", type: "text", fallback: "" },
      {
        property: "Active",
        selector: "#Active",
        type: "boolean",
        fallback: false,
      },
      {
        property: "Tooltip",
        selector: "#Tooltip",
        type: "text",
        fallback: "",
      },
      {
        property: "Types",
        selector: "#Types",
        type: "multiselect",
        fallback: [],
      },
    ],
    afterHydrate: (value) => {
      const key = value.Key ?? "";
      const isNew = value.New ?? false;

      const deleteButton = $("#DeleteButton").data("kendoButton") as
        | kendo.ui.Button
        | undefined;
      if (deleteButton) {
        deleteButton.enable(!(value.New ?? false));
      }

      const deleteButtonElement = document.getElementById("DeleteButton");
      if (deleteButtonElement) {
        deleteButtonElement.setAttribute(
          "onclick",
          `deleteConsideration('${key}')`,
        );
      }

      if (isNew) {
        setUrl();
      } else if (key) {
        setUrl(key);
        loadConsiderationOptions(key);
      }
    },
    formId: "ConsiderationsDetailsForm",
  });

function loadConsiderationOptions(key: string) {
  if (!key) return;

  $.ajax({
    url: `Considerations/GetOptions/${key}`,
    method: "GET",
    success: function (data) {
      considerationOptions = [...data];
    },
  });
}

function hydrateConsiderationForm(value: Consideration) {
  const form = hydrator.getForm();
  if (!form) {
    return;
  }

  hydrateKendoForm(value, hydrator);
}

function handleConsiderationResponse(response: unknown) {
  if (typeof response === "string") {
    hydrator.replaceForm(response);
    return;
  }

  if (response && typeof response === "object") {
    hydrateConsiderationForm(response as Consideration);
  }
}

function saveConsideration() {
  const form = hydrator.getForm();
  const notification = $("#popup_notification").data("kendoNotification")!;

  if (!form) {
    notification.show("Consideration was not found.", "error");
    return;
  }

  if (!form.checkValidity()) {
    notification.show("Please fill out all required fields.", "error");
    return;
  }

  $.ajax({
    url: "Considerations/Save",
    method: "POST",
    data: serializeForm(form),
    success: function (response) {
      notification.show("Consideration saved successfully!", "success");

      handleConsiderationResponse(response);
      $("#ConsiderationsGrid").data("kendoGrid")!.dataSource.read();
    },
    error: function () {
      notification.show(
        "An error occurred while saving the consideration. Please try again.",
        "error",
      );
    },
  });
}

function selectConsideration(e: HTMLButtonElement) {
  const tr = $(e).closest("tr");
  const grid = $("#ConsiderationsGrid").data("kendoGrid")!;

  const dataItem = grid.dataItem(tr);

  const currentSearch = window.location.search;

  const urlParams = new URLSearchParams(currentSearch);
  urlParams.set("rowKey", dataItem.get("Key"));

  const url = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${urlParams.toString()}`;

  window.history.pushState({ path: url }, "", url);

  const noConsiderationElement = document.getElementById("no-consideration");
  const considerationSelectedElement = document.getElementById(
    "consideration-selected-parent",
  );
  const considerationSelectedTextElement = document.getElementById(
    "consideration-selected-text",
  );

  if (!noConsiderationElement!.classList.contains("d-none")) {
    noConsiderationElement!.classList.add("d-none");
  }

  if (considerationSelectedElement!.classList.contains("d-none")) {
    considerationSelectedElement!.classList.remove("d-none");
    considerationSelectedTextElement!.textContent = dataItem.get("Name") || "";
  }

  $.ajax({
    url: `Considerations/Edit/${dataItem.get("Key")}`,
    method: "GET",
    success: function (data) {
      handleConsiderationResponse(data);
      grid.select(tr);
      //tabs.select(1);
    },
  });

  loadConsiderationOptions(dataItem.get("Key"));
  loadConsiderationOptionsTabs(dataItem.get("Key"));
}

function loadConsiderationOptionsTabs(key: string) {
  $.ajax({
    url: `Considerations/OptionEditor/Load/${key}`,
    method: "GET",
    success: function (data) {
      let tabs = document.getElementById("ConsiderationOptions")!;

      tabs.outerHTML = data.trim();

      $("#ConsiderationOptions").kendoTabStrip({})!;

      const tabData = $("#ConsiderationOptions").data("kendoTabStrip")!;

      for (let i = 0; i < considerationOptions.length; i++) {
        const tabContent = tabData.contentElement(i);

        executeScripts(
          Array.from(
            tabContent.querySelectorAll("script"),
          ) as HTMLScriptElement[],
        );
      }
    },
  });
}

function updateConsiderationTabName(
  event: kendo.ui.TextBoxChangeEvent | Event,
) {
  let newValue = "";

  if ("sender" in event) {
    newValue = event.sender.value();
  } else {
    const target = event.target ?? event.currentTarget;
    if (target instanceof HTMLInputElement) {
      newValue = target.value;
    }
  }

  const tabStrip = $("#ConsiderationOptions").data("kendoTabStrip")!;

  const selectedTab = tabStrip.select();
  const tabIndex = tabStrip.tabGroup.children().index(selectedTab);

  if (!Number.isInteger(tabIndex) || tabIndex < 0) {
    return;
  }

  considerationOptions[tabIndex]!.Name = newValue;

  const tabItem = $(tabStrip.items()[tabIndex]);
  const tabLabel = tabItem.find(".k-link-text");
  if (tabLabel.length > 0) {
    tabLabel.text(newValue);
    return;
  }

  const tabLink = tabItem.find(".k-link");
  if (tabLink.length > 0) {
    tabLink.text(newValue);
    return;
  }

  tabItem.text(newValue);
}

function considerationDataBounded() {
  const grid = $("#ConsiderationsGrid").data("kendoGrid")!;

  const urlParams = new URLSearchParams(window.location.search);
  const rowKey = urlParams.get("rowKey");

  if (rowKey) {
    const dataItem = grid.dataSource.get(rowKey);
    if (dataItem) {
      const tr = grid.tbody.find(`tr[data-uid="${dataItem.uid}"]`);
      grid.select(tr);
    }
  }
}

function addNewConsideration() {
  const notification = $("#popup_notification").data("kendoNotification")!;

  $.ajax({
    url: `Considerations/Create`,
    method: "GET",
    success: function (data) {
      const tabData = $("#ConsiderationOptions").data("kendoTabStrip")!;
      tabData.remove(tabData.tabGroup.children());
      considerationOptions = [];

      handleConsiderationResponse(data);
    },
    error: function () {
      notification.show("An error occurred. Please try again.", "error");
    },
  });
}

function deleteConsideration(key: string) {
  const notification = $("#popup_notification").data("kendoNotification")!;
  $.ajax({
    url: `Considerations/Delete/${key}`,
    method: "DELETE",
    success: function () {
      notification.show("Consideration deleted successfully!", "success");

      addNewConsideration();
      refreshConsiderationsRecords();
    },
    error: function () {
      notification.show(
        "An error occurred while deleting the consideration. Please try again.",
        "error",
      );
    },
  });
}

function refreshConsiderationsRecords() {
  const grid = $("#ConsiderationsGrid").data("kendoGrid")!;
  grid.dataSource.read();
  grid.refresh();
}

// Renders one option into a new tab and selects it. Shared by Add and Copy, which differ only
// in the object they hand over -- the server owns the editor markup either way.
async function appendConsiderationOptionTab(option: ConsiderationOption) {
  const index = considerationOptions.length;
  considerationOptions.push(option);

  const tabs = $("#ConsiderationOptions").data("kendoTabStrip")!;
  const response = await fetch(`Considerations/OptionEditor/${index}`, {
    method: "POST",
    body: JSON.stringify(option),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const formContent = await response.text();

  const template = document.createElement("template");
  template.innerHTML = formContent.trim();

  const tabCount = tabs.items().length;

  tabs.append({
    text: option.Name,
    content: template.innerHTML,
  });

  const contentElement = tabs.contentElement(tabCount);

  kendo.init(contentElement);

  tabs.select(tabCount);
}

async function addConsiderationOption() {
  await appendConsiderationOptionTab({
    Name: `Option ${considerationOptions.length + 1}`,
    FormKey: EmptyGuid,
    SortOrder: 0,
    Description: "",
    NotifySafety: false,
    RiskSeverityAdder: 0,
    RiskLikelihoodAdder: 0,
    SafetyHazards: "",
    Risk: "",
    ControlsOrMitigations: "",
    Key: crypto.randomUUID(),
    ConsiderationKey: EmptyGuid,
    QualificationAndRequirements: [],
  });
}

// Reads one editor field back out of the DOM. Every text-ish field on this form is a Kendo
// widget, and a widget's current value lives on the widget instance, NOT on the underlying
// input -- reading .value off the element returns whatever was rendered, so an edit made since
// load would be silently dropped from the copy. The plain <input> fallback covers the
// checkbox and the hidden keys.
function optionFieldValue(index: number, field: string): string {
  const el = $(`#ConsiderationOptions_${index}_${field}`);
  if (!el.length) return "";

  const widget =
    el.data("kendoTextBox") ??
    el.data("kendoTextArea") ??
    el.data("kendoNumericTextBox") ??
    el.data("kendoDropDownList");

  const value = widget ? widget.value() : (el[0] as HTMLInputElement).value;
  return value === null || value === undefined ? "" : String(value);
}

// Duplicates the option on the open tab into a new one on the SAME consideration. Values are
// read live rather than from the cached considerationOptions entry, which only tracks Name --
// a copy has to reflect what the user is looking at, including unsaved edits.
async function copyConsiderationOption() {
  const tabStrip = $("#ConsiderationOptions").data("kendoTabStrip");
  if (!tabStrip) return;

  const selectedTab = tabStrip.select();
  const index = tabStrip.tabGroup.children().index(selectedTab);
  if (!Number.isInteger(index) || index < 0) return;

  const qualifications = $(`#ConsiderationOptions_${index}_QualificationAndRequirements`)
    .data("kendoMultiSelect");
  const formKey = optionFieldValue(index, "FormKey");
  const sortOrder = Number(optionFieldValue(index, "SortOrder"));

  await appendConsiderationOptionTab({
    // Named so the copy is findable among identical siblings, and so it sorts immediately
    // after its source when the two share a display order.
    Name: `${optionFieldValue(index, "Name")} (copy)`,
    FormKey: formKey === "" ? EmptyGuid : formKey,
    // Kept, not reset: a copy is meant to sit beside its source, and the "(copy)" suffix
    // breaks the tie by name.
    SortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
    Description: optionFieldValue(index, "Description"),
    NotifySafety: $(`#ConsiderationOptions_${index}_NotifySafety`).is(":checked"),
    RiskSeverityAdder: Number(optionFieldValue(index, "RiskSeverityAdder")) || 0,
    RiskLikelihoodAdder: Number(optionFieldValue(index, "RiskLikelihoodAdder")) || 0,
    SafetyHazards: optionFieldValue(index, "SafetyHazards"),
    Risk: optionFieldValue(index, "Risk"),
    ControlsOrMitigations: optionFieldValue(index, "ControlsOrMitigations"),
    // A fresh key: the copy is a new row, and reusing the source's would overwrite it on save.
    Key: crypto.randomUUID(),
    ConsiderationKey: optionFieldValue(index, "ConsiderationKey") || EmptyGuid,
    QualificationAndRequirements: qualifications ? [...qualifications.value()] : [],
  });
}

function getConsiderationValue(id: string) {
  const multiSelect = $(id).data("kendoMultiSelect")!;

  return {
    values: multiSelect.value() || [],
  };
}

function onConsiderationChange(event: kendo.ui.MultiSelectChangeEvent) {
  const multiSelect = event.sender.element;
  const multiSelectId = multiSelect.attr("id");

  const optionsElementId = multiSelect.attr("data-options");
  const optionMultiSelect = $(optionsElementId!).data("kendoMultiSelect")!;

  optionMultiSelect.refresh();
  optionMultiSelect.dataSource.read(getConsiderationValue(`#${multiSelectId}`));
}

function deleteConsiderationOption(optionIndex: number) {
  const optionKey = considerationOptions[optionIndex]!.Key;
  deleteOptions(optionKey, optionIndex);
}

function deleteOptions(key: string, index: number) {
  const tabs = $("#ConsiderationOptions").data("kendoTabStrip")!;
  const notification = $("#popup_notification").data("kendoNotification")!;
  $.ajax({
    url: `Considerations/Option/Delete/${key}`,
    method: "DELETE",
    success: function () {
      notification.show("Option deleted successfully!", "success");
      tabs.remove(index);
      tabs.select(0);
    },
    error: function () {
      notification.show(
        "An error occurred while deleting the option. Please try again.",
        "error",
      );
    },
  });
}

Object.assign(globalThis as typeof globalThis, {
  addNewConsideration,
  deleteConsideration,
  hydrateConsiderationForm,
  saveConsideration,
  selectConsideration,
  considerationDataBounded,
  addConsiderationOption,
  copyConsiderationOption,
  loadConsiderationOptions,
  updateConsiderationTabName,
  getConsiderationValue,
  onConsiderationChange,
  deleteConsiderationOption,
});
